import React, { Fragment, useMemo } from 'react';
import { ChecklistDark } from '@otp/icons';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Card, Typography } from '@otp/ui';
import { Routes } from 'src/components/common/router/constants';
import { useNavigate } from 'react-router-dom';
import { useIssueTicketsStats } from 'src/hooks/query/use-issue-ticket-stats';
import { useIssueTickets } from 'src/hooks/query/use-issue-tickets';
import {
  IssueTicketStatus,
  IssueTicketImportance,
  SortingDirection,
} from 'src/graphql/generated/graphql';
import { getFullName } from 'src/utils/user-data';

const wttpStatuses = [
  IssueTicketStatus.Open,
  IssueTicketStatus.InProgress,
  IssueTicketStatus.Blocked,
];

const wttpStats = [
  {
    key: 'high' as const,
    label: 'my-organisation.wttp.open-tasks.high',
  },
  {
    key: 'medium' as const,
    label: 'my-organisation.wttp.open-tasks.medium',
  },
  {
    key: 'low' as const,
    label: 'my-organisation.wttp.open-tasks.low',
  },
  {
    key: 'over90Days' as const,
    label: 'my-organisation.wttp.open-tasks.over-90-days',
  },
];

export const OpenTasks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const filter = {
    isWttpIssue: true,
    status: wttpStatuses,
  };

  const { stats, loading: wttpStatsLoading } = useIssueTicketsStats({
    filter,
  });

  const { issueTickets, loading } = useIssueTickets({
    filter,
    sorting: {
      createdAt: SortingDirection.Desc,
    },
    pagination: {
      limit: 3,
      offset: 0,
    },
  });

  const getImportanceColor = (importance: IssueTicketImportance) => {
    switch (importance) {
      case IssueTicketImportance.High:
        return 'text-red-500 bg-red-50';
      case IssueTicketImportance.Medium:
        return 'text-yellow-900 bg-yellow-200';
      default:
        return 'text-blue-500 bg-blue-200';
    }
  };

  const getImportanceLabel = (importance: IssueTicketImportance) => {
    switch (importance) {
      case IssueTicketImportance.High:
        return t('my-organisation.wttp.open-tasks.high');
      case IssueTicketImportance.Medium:
        return t('my-organisation.wttp.open-tasks.medium');
      default:
        return t('my-organisation.wttp.open-tasks.low');
    }
  };

  return (
    <Grid item xs={12}>
      <Card className="bg-[#F7F9FD] p-4 border-none w-full h-full">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              isLink
              onClick={() => navigate(Routes.issueTickets.path)}
              className="w-full border-none bg-[#E7EEFF] hover:bg-gray-100 transition-colors px-4 rounded-3xl"
              linkPosition="top"
            >
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={4}
                  container
                  direction="column"
                  spacing={1}
                  gap={1}
                >
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    className="text-2xl md:text-3xl lg:text-xl leading-tight"
                  >
                    {stats?.totalCount}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="text-gray-600 text-sm md:text-base"
                  >
                    {t('my-organisation.wttp.open-tasks.total-tasks')}
                  </Typography>
                </Grid>

                <Grid item xs={1} container justifyContent="center">
                  <div className="w-px h-[100%] bg-gray-300" />
                </Grid>

                <Grid
                  item
                  container
                  xs={6}
                  direction="column"
                  spacing={1}
                  gap={1}
                >
                  <Grid container spacing={3}>
                    {wttpStats?.map((stat) => (
                      <Grid key={stat.key} item xs={6} md={3}>
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          className="text-base md:text-lg"
                        >
                          {stats?.[stat.key]}
                        </Typography>
                        <Typography
                          variant="body2"
                          className="text-gray-600 text-xs md:text-sm whitespace-nowrap"
                        >
                          {t(stat.label)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* List Section */}
          <Grid container item xs={12} gap={3}>
            {issueTickets?.length ? (
              issueTickets?.map((ticket) => (
                <Fragment key={ticket?.id}>
                  <Grid container alignItems="center" className="mb-[-8px]">
                    <Grid xs={10}>
                      <Grid container alignItems="center" gap={2}>
                        <Typography
                          variant="caption"
                          className="text-[#005EB3] bg-[#D5E3FF] rounded-3xl px-3"
                        >
                          #{ticket?.idNumber}
                        </Typography>
                        <Typography
                          variant="caption"
                          className={`px-2 py-0.5 rounded-3xl ${getImportanceColor(ticket?.importance)}`}
                        >
                          {getImportanceLabel(ticket?.importance)}
                        </Typography>
                      </Grid>
                      <Typography variant="body1" fontWeight="bold">
                        {ticket?.name}
                      </Typography>
                      <Typography variant="body2" className="text-gray-600">
                        {getFullName(ticket?.assignedTo)
                          ? `${t('my-organisation.wttp.assigned-to')} ${getFullName(ticket?.assignedTo)}`
                          : t('my-organisation.wttp.not-assigned')}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} container justifyContent="flex-end">
                      {ticket?.stores.slice(0, 1)?.map((store) => (
                        <Typography
                          key={store?.id}
                          variant="body2"
                          className="text-white bg-blue-500 px-3 py-1 rounded-full"
                        >
                          {store?.nationalStoreNumber}
                        </Typography>
                      ))}
                    </Grid>
                  </Grid>
                  <hr className="w-full h-[1px] bg-blue-300/40 border-0 my-0.5" />
                </Fragment>
              ))
            ) : (
              <Grid item xs={12} container justifyContent="center">
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  className="text-gray-600"
                >
                  {t('my-organisation.wttp.open-tasks.no-tasks')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
};

export default OpenTasks;
